import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';

const UserWhatsAppSettings = () => {
  const { locationId, userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [locationInfo, setLocationInfo] = useState(null);
  const [sharingMode, setSharingMode] = useState('SHARED');
  const [qrUpdatedAt, setQrUpdatedAt] = useState(null);
  const [qrIsStale, setQrIsStale] = useState(false);
  const [lockedByOther, setLockedByOther] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);

  const viewerIdRef = useRef(null);
  const isHolderRef = useRef(false);
  const intervalRef = useRef(null);
  const heartbeatRef = useRef(null);

  const getViewerId = () => {
    if (viewerIdRef.current) return viewerIdRef.current;
    const key = `qrViewerId:${locationId}:${userId || 'shared'}`;
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = (window.crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(key, id);
    }
    viewerIdRef.current = id;
    return id;
  };

  const lockBody = () => {
    const body = { locationId, viewerId: getViewerId() };
    if (userId) body.userId = userId;
    return body;
  };

  const postLock = async (path) => {
    const resp = await fetch(`/whatsapp/qr-viewer/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(lockBody()),
    });
    return resp.ok ? resp.json() : null;
  };

  const stopTimers = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const startHolderTimers = () => {
    stopTimers();
    intervalRef.current = setInterval(() => {
      if (isHolderRef.current) fetchQRCode();
    }, 5000);
    heartbeatRef.current = setInterval(async () => {
      if (!isHolderRef.current) return;
      const data = await postLock('heartbeat');
      if (data?.locked) {
        isHolderRef.current = false;
        stopTimers();
        setLockedByOther(true);
        setQrCode(null);
      }
    }, 10000);
  };

  useEffect(() => {
    fetchUserInfo();
    fetchLocationInfo();
    claimAndStart();

    return () => {
      stopTimers();
      if (isHolderRef.current && locationId && viewerIdRef.current) {
        try {
          navigator.sendBeacon(
            '/whatsapp/qr-viewer/release',
            new Blob([JSON.stringify(lockBody())], { type: 'application/json' }),
          );
        } catch (e) { /* ignore */ }
      }
    };
  }, [locationId, userId]);

  const claimAndStart = async () => {
    setLoading(true);
    try {
      const claim = await postLock('claim');
      if (claim?.holder) {
        isHolderRef.current = true;
        setLockedByOther(false);
        await fetchQRCode();
        startHolderTimers();
      } else {
        isHolderRef.current = false;
        setLockedByOther(true);
        setQrCode(null);
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to claim QR page lock');
      setLoading(false);
    }
  };

  const handleUseHere = async () => {
    setLockBusy(true);
    try {
      const data = await postLock('takeover');
      if (data?.holder) {
        isHolderRef.current = true;
        setLockedByOther(false);
        await fetchQRCode();
        startHolderTimers();
      }
    } finally {
      setLockBusy(false);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
    }
  };

  const fetchLocationInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/whatsapp/sessions/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSharingMode(data.sharingMode || 'SHARED');
        const user = data.userSessions.find(u => u.userId === userId);
        setLocationInfo({
          locationId: data.locationId,
          locationName: data.locationName,
          userName: user?.name,
          userEmail: user?.email,
        });
      }
    } catch (err) {
      console.error('Error fetching location info:', err);
    }
  };

  const fetchQRCode = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/whatsapp/qr/${locationId}/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.hasQR && data.qrCode) {
          setQrCode(data.qrCode);
          setError('');
          const updatedAt = data.qrUpdatedAt || null;
          setQrUpdatedAt(updatedAt);
          setQrIsStale(updatedAt ? (Date.now() - updatedAt > 20000) : false);
        } else {
          setQrCode(null);
          setError(data.message || 'No QR code available');
        }
      } else if (response.status === 403) {
        setError('Unauthorized to view this QR code');
      } else {
        setError('Failed to fetch QR code');
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Network error while fetching QR code');
    } finally {
      setLoading(false);
    }
  };

  const initializeWhatsApp = async () => {
    if (!isHolderRef.current) {
      setError('QR page is open in another window. Click Use here first.');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/whatsapp/init-user/${locationId}/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setError('');
        setTimeout(() => fetchQRCode(), 2000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to initialize WhatsApp');
      }
    } catch (err) {
      setError('Network error while initializing WhatsApp');
    }
  };

  if (loading) {
    return (
      <DashboardLayout userInfo={userInfo}>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/login');
  };

  return (
    <DashboardLayout userInfo={userInfo} onLogout={handleLogout}>
      <div className="px-4 py-8 max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate(`/user-management/${locationId}`)}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
          >
            ← Back to User Management
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-bold mb-2">WhatsApp QR Code</h1>
            {locationInfo && (
              <div className="text-gray-600 space-y-1">
                <p className="text-sm">Location: {locationId}</p>
                <p className="font-semibold">{locationInfo.userName}</p>
                <p className="text-sm">{locationInfo.userEmail}</p>
                <div className="mt-3">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    sharingMode === 'SHARED' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {sharingMode} MODE
                  </span>
                </div>
              </div>
            )}
          </div>

        {lockedByOther && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 mb-6 text-center">
            <p className="text-lg font-semibold text-indigo-900 mb-2">
              QR page is open in another window
            </p>
            <p className="text-sm text-indigo-700 mb-4">
              Close the other tab, or click <strong>Use here</strong> to continue on this device.
              Switching may interrupt an in-progress scan.
            </p>
            <button
              onClick={handleUseHere}
              disabled={lockBusy}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded font-semibold disabled:opacity-60"
            >
              {lockBusy ? 'Taking over...' : 'Use here'}
            </button>
          </div>
        )}

        {!lockedByOther && error && !qrCode && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-6">
            <p className="font-semibold">No QR Code Available</p>
            <p className="text-sm mt-2">{error}</p>
            <button
              onClick={initializeWhatsApp}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold"
            >
              Initialize WhatsApp
            </button>
          </div>
        )}

          {!lockedByOther && qrCode && !qrIsStale && (
            <div className="flex flex-col items-center">
              {sharingMode === 'SHARED' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 w-full max-w-md">
                  <p className="text-sm text-purple-800 text-center">
                    <strong>Shared Mode:</strong> This QR code is shared across all users in this location.
                  </p>
                </div>
              )}
              
              <div className="bg-white p-6 rounded-lg border-4 border-gray-200 mb-4">
                <img 
                  src={qrCode} 
                  alt="WhatsApp QR Code" 
                  className="w-80 h-80"
                />
              </div>
              <div className="text-center space-y-2">
                <p className="text-sm text-gray-600">
                  <strong>Steps to connect:</strong>
                </p>
                <ol className="text-sm text-gray-600 text-left max-w-md mx-auto space-y-1">
                  <li>1. Open WhatsApp on your phone</li>
                  <li>2. Go to Settings &#8594; Linked Devices</li>
                  <li>3. Tap "Link a Device"</li>
                  <li>4. Scan this QR code</li>
                </ol>
              </div>
              <div className="mt-6 text-xs text-gray-500">
                QR code updates automatically
              </div>
            </div>
          )}

          {!lockedByOther && qrCode && qrIsStale && (
            <div className="flex flex-col items-center py-8">
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6 text-center max-w-sm">
                <p className="text-2xl mb-3">⚠️</p>
                <p className="text-lg font-semibold text-yellow-800 mb-2">
                  QR code is no longer refreshing.
                </p>
                <p className="text-sm text-yellow-700 mb-5">
                  Evolution stopped sending new QR codes. Re-initialize to get a fresh one.
                </p>
                <button
                  onClick={initializeWhatsApp}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded font-semibold"
                >
                  Initialize WhatsApp
                </button>
              </div>
            </div>
          )}

        {!lockedByOther && error && qrCode && (
          <div className="mt-4 bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded text-center">
            <p className="text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
    </DashboardLayout>
  );
};

export default UserWhatsAppSettings;
